import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import InnerHTML from '@Components/InnerHTML';
import { COMPOUND_ID_PREFIXES, SUBSTANCE_DETAILS_TOOLTIPS } from '@Constants/ElementConstants';
import { getValueByNs } from '@Hooks/LibraryInitialization/MoreUnderscore';
import isEmpty from 'lodash/isEmpty';
import isArray from 'lodash/isArray';
import { ButtonWithIcon } from '@els/els-react--button';
import { LinkElement } from '@els/els-react--link-element';

import './SubstanceIdentificationInformation.scss';

const IDENTIFICATION_INFORMATION_CLASS = 'rx-substance-identification';
const ROW_VARIANTS = {
  SINGLE_COLUMN: 'single-column',
};

function getCompoundId(id, db) {
  const prefix = COMPOUND_ID_PREFIXES[db.databaseId];
  return prefix ? `${prefix}-${id}` : id;
}

function getJoinedValue(value) {
  if (isEmpty(value)) {
    return null;
  }

  return isArray(value) ? value.join(', ') : value;
}

function getCompoundTypes(type) {
  if (!type) {
    return null;
  }

  return Array.isArray(type) ? type.join(', ') : type;
}

function getDescriptionListClassName(type, rowVariant) {
  return classnames(`${IDENTIFICATION_INFORMATION_CLASS}__${type}`, {
    [`${IDENTIFICATION_INFORMATION_CLASS}__${type}--${rowVariant}`]: Boolean(rowVariant),
  });
}

function renderDescriptionListEntry(label, content, rowVariant = ROW_VARIANTS.SINGLE_COLUMN) {
  if (!content) {
    return null;
  }

  return (
    <>
      <dt className={getDescriptionListClassName('term', rowVariant)}>{label}</dt>
      <dd className={getDescriptionListClassName('description', rowVariant)}>{content}</dd>
    </>
  );
}

function SubstanceIdentificationInformation({ substance, database, onToggle }) {
  const { details, sourceUrl } = substance;
  const mapped = SUBSTANCE_DETAILS_TOOLTIPS[database.id];
  const label = getValueByNs(mapped, 'id') || `${database.name} ID`;
  const compoundId = details.id ? getCompoundId(details.id, database) : null;
  const casRegistryNumbers = getJoinedValue(details.casRegistryNumbers);
  const compoundTypes = getCompoundTypes(details.type);

  // Keyboard accessibility for close icon
  const handleCloseKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      onToggle();
    }
  };

  return (
    <div
      id="substance-identification-information"
      className="substance-results-information u-els-padding-top u-els-font-size-base u-els-line-height-default"
      aria-expanded>
      <div className="sub-menu u-els-padding u-els-background-color-n0">
        <div className="substance-identification-heading">
          <h3>Identification</h3>
          <ButtonWithIcon
            data-e2e="substance-identification-information-close"
            type="link"
            iconOnly
            sprite="Close"
            tabIndex="0"
            role="button"
            onClick={onToggle}
            onKeyDown={handleCloseKeyDown}
            aria-label="Close">
            close
          </ButtonWithIcon>
        </div>

        <dl
          className={`${IDENTIFICATION_INFORMATION_CLASS} ${IDENTIFICATION_INFORMATION_CLASS}--description-list results-key-value-wrapper`}>
          {renderDescriptionListEntry(
            `${label}:`,
            sourceUrl ? (
              <LinkElement data-e2e="substance-identification-information-link" href={sourceUrl} isExternal>
                {compoundId}
              </LinkElement>
            ) : (
              <InnerHTML tag="span" html={compoundId} />
            )
          )}
          {renderDescriptionListEntry(
            'CAS Registry Number(s):',
            casRegistryNumbers ? <InnerHTML tag="span" html={casRegistryNumbers} /> : null
          )}
          {renderDescriptionListEntry(
            'Molecular Formula:',
            details.formula ? <InnerHTML tag="span" html={details.formula} mode="escape" /> : null
          )}
          {renderDescriptionListEntry(
            'Linear Structure Formula:',
            details.linearFormula ? <InnerHTML tag="span" html={details.linearFormula} mode="escape" /> : null
          )}
          {renderDescriptionListEntry('Molecular Weight:', details.weight ? <InnerHTML tag="span" html={details.weight} /> : null)}
          {renderDescriptionListEntry('InChIKey:', details.inChiKey ? <InnerHTML tag="span" html={details.inChiKey} /> : null)}
          {renderDescriptionListEntry('Compound Type(s):', compoundTypes ? <InnerHTML tag="span" html={compoundTypes} /> : null)}
        </dl>
      </div>
    </div>
  );
}

SubstanceIdentificationInformation.propTypes = {
  database: PropTypes.object.isRequired,
  substance: PropTypes.object.isRequired,
  onToggle: PropTypes.func.isRequired,
};

export default SubstanceIdentificationInformation;
